import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, ChevronLeft, ChevronRight, Eye,
  ArrowUpDown, ArrowUp, ArrowDown, X, Layers, Lock,
} from 'lucide-react';
import { groupsApi } from '../../services/api';
import type { Group, Pagination } from '../../types';
import GroupDetailModal from '../projects/GroupDetailModal';

/* ───────── Helpers ───────── */

type SortCol = 'name' | 'owner' | 'type' | 'modified_date';
type SortDir = 'asc' | 'desc';

const TYPE_LABELS: Record<string, string> = { V: 'Validation', R: 'Reconciliation' };
const TYPE_COLORS: Record<string, string> = {
  V: 'bg-green-500/20 text-green-400 border-green-500/30',
  R: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

function typeBadge(type: string) {
  const cls = TYPE_COLORS[type] || 'bg-cargo-panel text-cargo-muted border-cargo-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function formatDate(d: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

/* ───────── Component ───────── */

const GroupsPage: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('modified_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [detailGroup, setDetailGroup] = useState<Group | null>(null);

  /* ── Fetch groups ── */
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page, items_per_page: perPage, sort_col: sortCol, sort_dir: sortDir,
      };
      if (search.trim()) params.search = search.trim();

      const resp = await groupsApi.list(params);
      const payload = resp.data.payload;
      const list = payload?.groups || payload || [];
      setGroups(Array.isArray(list) ? list : []);
      const pag: Pagination | undefined = payload?.pagination;
      if (pag) {
        setCount(pag.count || 0);
        setPages(pag.pages || 1);
      } else {
        setCount(Array.isArray(list) ? list.length : 0);
        setPages(1);
      }
    } catch {
      setGroups([]);
      setCount(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, sortCol, sortDir]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  /* ── Sort ── */
  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
    setPage(1);
  };

  function SortIcon({ col }: { col: SortCol }) {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  /* ── Render ── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Layers className="w-6 h-6 text-cargo-accent" />
          <h1 className="text-2xl font-bold text-cargo-text">Groups</h1>
        </div>
        <p className="text-sm text-cargo-muted mt-1">{count} group{count !== 1 ? 's' : ''}</p>
      </div>

      {/* Search */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search groups by name..."
            className="w-full pl-10 pr-8 py-2.5 rounded-lg bg-cargo-panel border border-cargo-border
                       text-cargo-text placeholder-cargo-muted/50 text-sm
                       focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-cargo-muted hover:text-cargo-text"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cargo-border">
                {([
                  ['name', 'Group Name', true],
                  ['type', 'Type', true],
                  ['owner', 'Owner', true],
                  ['', 'Project', false],
                  ['modified_date', 'Modified', true],
                  ['', '', false],
                ] as [string, string, boolean][]).map(([col, label, sortable], i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider
                                ${sortable ? 'cursor-pointer hover:text-cargo-text select-none' : ''}`}
                    onClick={sortable ? () => handleSort(col as SortCol) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortable && col && <SortIcon col={col as SortCol} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
                    <p className="text-cargo-muted text-sm mt-2">Loading groups...</p>
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-cargo-muted text-sm">
                    No groups found.
                  </td>
                </tr>
              ) : (
                groups.map((g) => (
                  <tr
                    key={g.id}
                    className="border-b border-cargo-border/50 hover:bg-cargo-panel/50 transition-colors cursor-pointer"
                    onClick={() => setDetailGroup(g)}
                  >
                    {/* Name */}
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="flex items-center gap-1.5">
                        <p className="text-cargo-text font-medium text-sm truncate">{g.name}</p>
                        {g.meta?.locked && <Lock className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                      </div>
                      {g.description && (
                        <p className="text-xs text-cargo-muted mt-0.5 truncate" title={g.description}>{g.description}</p>
                      )}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">{typeBadge(g.type)}</td>

                    {/* Owner */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{g.owner}</td>

                    {/* Project */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{g.project?.name || '—'}</td>

                    {/* Modified */}
                    <td className="px-4 py-3 text-cargo-muted whitespace-nowrap">{formatDate(g.modified_date)}</td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDetailGroup(g); }}
                        className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors"
                        title="Manage rules"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && groups.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-cargo-border">
            <div className="flex items-center gap-2 text-sm text-cargo-muted">
              <span>Rows per page:</span>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="pl-2 pr-7 py-1 rounded bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                           outline-none focus:ring-0 focus:border-cargo-accent cursor-pointer"
              >
                {[10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-cargo-muted">Page {page} of {pages}</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                             disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel
                             disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal — shows rules with add/edit/delete */}
      {detailGroup && (
        <GroupDetailModal
          group={detailGroup}
          onClose={() => setDetailGroup(null)}
        />
      )}
    </div>
  );
};

export default GroupsPage;
